import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Google,
  Facebook,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { login, googleAuth, facebookAuth } from '../../store/authSlice';
import { showToast } from '../../store/uiSlice';
import Logo from '../../assets/header-logo.svg';
import { LivingBoardPreview } from './LivingBoardPreview';

// Declare Facebook SDK type
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  // Check if OAuth credentials are configured
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '';
  const isGoogleConfigured =
    googleClientId &&
    !googleClientId.includes('your-google-client-id') &&
    googleClientId.includes('.apps.googleusercontent.com');
  const isFacebookConfigured =
    facebookAppId && !facebookAppId.includes('your-facebook-app-id');

  // Initialize Facebook SDK
  useEffect(() => {
    if (!isFacebookConfigured) return;

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: facebookAppId,
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
    };

    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode?.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }, [isFacebookConfigured, facebookAppId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Send username as userName to match backend API expectations
      await dispatch(login({ userName: username, password })).unwrap();
      // Only navigate if login was successful
      dispatch(showToast({
        message: 'Login successful! Welcome back.',
        type: 'success'
      }));
      navigate('/workspaces');
    } catch (err: any) {
      // Show error notification
      const errorMessage = err?.message || 'Login failed. Please check your credentials.';
      dispatch(showToast({
        message: errorMessage,
        type: 'error'
      }));
      console.error('Login failed:', err);
    }
  };

  // Google Login Handler
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        await dispatch(
          googleAuth({
            credential: tokenResponse.access_token,
          })
        ).unwrap();
        dispatch(showToast({
          message: 'Login successful! Welcome back.',
          type: 'success'
        }));
        navigate('/dashboard');
      } catch (err: any) {
        const errorMessage = err?.message || 'Google login failed. Please try again.';
        dispatch(showToast({
          message: errorMessage,
          type: 'error'
        }));
        console.error('Google login failed:', err);
      }
    },
    onError: () => {
      dispatch(showToast({
        message: 'Google login cancelled or failed. Please try again.',
        type: 'error'
      }));
      console.error('Google login failed');
    },
  });

  const handleGoogleLogin = () => {
    if (!isGoogleConfigured) {
      alert(
        'Google OAuth is not configured. Please add your Google Client ID to the .env file.\n\nSee OAUTH_SETUP.md for detailed setup instructions.'
      );
      return;
    }
    googleLogin();
  };

  // Facebook Login Handler
  const handleFacebookLogin = () => {
    if (!isFacebookConfigured) {
      alert(
        'Facebook OAuth is not configured. Please add your Facebook App ID to the .env file.\n\nSee OAUTH_SETUP.md for detailed setup instructions.'
      );
      return;
    }
    if (window.FB) {
      window.FB.login(
        (response: any) => {
          if (response.authResponse) {
            dispatch(
              facebookAuth({
                accessToken: response.authResponse.accessToken,
                userID: response.authResponse.userID,
              })
            )
              .unwrap()
              .then(() => {
                dispatch(showToast({
                  message: 'Login successful! Welcome back.',
                  type: 'success'
                }));
                navigate('/dashboard');
              })
              .catch((err: any) => {
                const errorMessage = err?.message || 'Facebook login failed. Please try again.';
                dispatch(showToast({
                  message: errorMessage,
                  type: 'error'
                }));
                console.error('Facebook login failed:', err);
              });
          } else {
            dispatch(showToast({
              message: 'Facebook login cancelled. Please try again.',
              type: 'warning'
            }));
            console.error('Facebook login cancelled');
          }
        },
        { scope: 'public_profile,email' }
      );
    } else {
      dispatch(showToast({
        message: 'Facebook SDK not loaded. Please refresh and try again.',
        type: 'error'
      }));
      console.error('Facebook SDK not loaded');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#f7f8fc',
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: 'white',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left Side - Branding: a living kanban board, not a stock gradient */}
          <Box
            sx={{
              flex: { xs: 'none', md: 1 },
              background: 'linear-gradient(160deg, #0b1f3a 0%, #0f2b52 55%, #0073ea 140%)',
              p: { xs: 5, md: 7 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: { xs: '420px', md: 'auto' },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 420,
                height: 420,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(79,172,254,0.18) 0%, transparent 70%)',
                top: -160,
                right: -140,
                pointerEvents: 'none',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                <Box
                  component="img"
                  src={Logo}
                  alt="Agile Edge Logo"
                  sx={{ height: 40, width: 'auto', filter: 'brightness(0) invert(1)' }}
                />
                <Typography variant="h6" fontWeight="700" sx={{ textTransform: 'lowercase' }}>
                  agile edge
                </Typography>
              </Box>

              <Typography variant="h3" fontWeight="700" sx={{ mb: 1.5, letterSpacing: '-0.02em' }}>
                Where sprints actually finish.
              </Typography>
              <Typography variant="body1" sx={{ mb: 5, opacity: 0.72, maxWidth: 400 }}>
                Boards, sprints, and reports for teams who'd rather build than track things down.
              </Typography>

              <LivingBoardPreview />
            </Box>
          </Box>

          {/* Right Side - Login Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              maxWidth: { xs: '100%', md: '500px' },
              mx: 'auto',
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
                Log in
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome back — enter your details below.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email or Username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your Gmail or username"
                required
                autoComplete="email"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot Password link */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: '0.85rem',
                    color: '#0073ea',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 3,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #0073ea 0%, #4facfe 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0060b9 0%, #2f9dfd 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Stack spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                startIcon={<Google />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    borderColor: '#d2d3d4',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Continue with Google
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleFacebookLogin}
                startIcon={<Facebook />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    borderColor: '#d2d3d4',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Continue with Facebook
              </Button>
            </Stack>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  style={{
                    color: '#0073ea',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  Sign up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
